// Дела «Потребує уваги» и числа у пунктов меню (F-013, решение 066).
//
// ⚠⚠ Дело и число считает сервер из текущих чисел экранов (`api/attention.py`);
// клиент только раскладывает ответ: одно число на пункт. Ни одного счёта дел здесь
// нет (охрана арифметики не пускает и суммы штук), а «не знаю» не превращается в
// ноль: молчащий источник даёт значок «?» (правило 10).

import type { AttentionItem } from "@/api/schemas";
import { fill, plural, w, type Plural } from "@/api/words";
import { formatCount, formatMoney } from "@/money/format";

import { uk } from "../i18n/uk";

/** «· невідомо: N» — сколько ещё может быть делом (`unsure`): число дела занижено (правило 10). */
function unsureOf(item: AttentionItem): string {
  return item.unsure ? ` · ${fill(uk.attention.unsure, { n: formatCount(item.unsure) })}` : "";
}

/** Число у пункта меню. `count` `null` — источник не ответил: «?»; известных 0, а «невідомо» есть — тоже «?». */
export interface Badge {
  /** Полная фраза — для `title` и чтеца («40 звірок чекають підтвердження»). */
  readonly text: string;
  /** Что стоит на пункте: только цифра; «!» у сбоя, «?» у молчащего источника. */
  readonly digit: string;
  readonly tone: "alarm" | "neutral";
  readonly count: number | null;
}

/** Дело-сбой: «синхронизация упала», «копия не снялась» — слово без числа. */
const FAILURES: ReadonlySet<string> = new Set(["sync_down", "backup_failed"]);

/** Слово со счётом: одна строка или три формы (`Intl.PluralRules`) — склонение в одном месте. */
function counted(word: string | Plural, n: number): string {
  return typeof word === "string" ? fill(word, { n: formatCount(n) }) : plural(word, n, formatCount(n));
}

/**
 * Число на пункт — не больше одного: берётся самое срочное дело его экрана
 * (сервер отдаёт дела по срочности). Тон аварии — у просроченного и у сбоев.
 */
export function badgesOf(items: readonly AttentionItem[]): ReadonlyMap<string, Badge> {
  const out = new Map<string, Badge>();
  for (const one of items) {
    if (out.has(one.screen)) continue;
    const word: string | Plural = uk.attention.badges[one.key];
    out.set(
      one.screen,
      one.count === null
        ? { text: uk.attention.unknown, digit: "?", tone: one.tone, count: null }
        : one.count === 0
          ? { text: `${uk.attention.titles[one.key]}${unsureOf(one)}`, digit: "?", tone: one.tone, count: null }
          : {
              text: FAILURES.has(one.key) ? (word as string) : `${counted(word, one.count)}${unsureOf(one)}`,
              digit: FAILURES.has(one.key) ? "!" : formatCount(one.count),
              tone: one.tone,
              count: one.count,
            },
    );
  }
  return out;
}

/** Дело одной строкой: «3 прострочені оплати», «40 звірок чекають», «Прострочені оплати: не знаю». */
export interface Row {
  readonly text: string;
  /** Источник не ответил: после текста — «Перевірити», причина — в `hint`. */
  readonly unknown: boolean;
  /** Подсказка строки (`title`): причина «не знаю» или сумма дела. */
  readonly hint: string | null;
}

export function rowOf(item: AttentionItem): Row {
  const title = uk.attention.titles[item.key];
  if (item.count === null) {
    return { text: `${title}: ${uk.attention.unknown}`, unknown: true, hint: w(item.reason ?? "attention_source_failed") };
  }
  if (FAILURES.has(item.key)) return { text: title, unknown: false, hint: null };
  // Известных 0, а «невідомо» есть: не «0 постачальників», а «… · невідомо: N».
  const known = item.count === 0 ? title : counted(uk.attention.rows[item.key as keyof typeof uk.attention.rows], item.count);
  return {
    text: `${known}${unsureOf(item)}`,
    unknown: false,
    hint: item.unsure
      ? uk.attention.unsureHint
      : item.amount === null ? null : formatMoney({ amount: item.amount, currency: "UAH" }, item.key),
  };
}
